export const QA_QC_GREEN = "#22C55E";
export const QA_QC_ORANGE = "#F59E0B";
export const QA_QC_BLUE = "#2298D5";
export const QA_QC_RED = "#EF4444";

export function emptyDistributionViewData() {
  return {
    plotKind: "empty",
    xAxisTitle: "",
    rawValues: [],
    categories: [],
    categoryCounts: [],
    barColors: [],
    legendItems: [],
    summaryStats: null,
    showBinsSlider: false,
    validRange: { values: [], label: "Valid Range", count: "-", percent: "-" },
    outsideRange: { count: "-", label: "Outside Range - not available" },
    outsideRangeChipFlags: null,
    outsideRangeChipsByCategory: null,
    distributionChipsByCategory: null,
    failedHistogramChips: null,
    distributionPlotApiName: "",
    distributionCategoryApiPlotKeys: null,
  };
}

function parseValuesRaw(raw) {
  let r = raw;
  if (typeof r === "string") {
    const t = r.trim();
    if (t.startsWith("[") && t.endsWith("]")) {
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) r = parsed;
      } catch {
        /* keep string */
      }
    }
  }
  return r;
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

function fmtNum(n) {
  if (!Number.isFinite(n)) return "-";
  const abs = Math.abs(n);
  const d = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return n.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: 0 });
}

function unavailableViewData(plot) {
  return {
    plotKind: "unavailable",
    xAxisTitle: plot.DisplayName || plot.Name || "",
    rawValues: [],
    categories: [],
    categoryCounts: [],
    barColors: [],
    legendItems: [],
    summaryStats: null,
    showBinsSlider: false,
    validRange: { values: [], label: "", count: "-", percent: "" },
    outsideRange: { count: "-", label: "" },
    outsideRangeChipFlags: null,
    outsideRangeChipsByCategory: null,
    distributionChipsByCategory: null,
    failedHistogramChips: null,
    distributionPlotApiName: String(plot?.Name ?? plot?.name ?? "").trim(),
    distributionCategoryApiPlotKeys: null,
  };
}

/** Numeric series for histograms / summaries from `Values` only (not `Sample`). */
export function valuesFromPlot(plot) {
  if (!plot) return [];
  const raw = parseValuesRaw(plot.Values);
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map(toNum).filter((n) => !Number.isNaN(n));
}

export function failedSamplesFromPlot(plot) {
  if (!plot) return [];
  const raw = plot.FailedSamples ?? plot.failedSamples;
  if (!Array.isArray(raw)) return [];
  return raw.map((fs) => ({
    id: fs.ID != null && fs.ID !== "" ? String(fs.ID) : fs.id != null ? String(fs.id) : "",
    fluidSampleId:
      fs.FluidSampleID != null && fs.FluidSampleID !== ""
        ? String(fs.FluidSampleID)
        : fs.fluidSampleID != null
          ? String(fs.fluidSampleID)
          : "",
    well: fs.Well != null ? String(fs.Well) : fs.well != null ? String(fs.well) : "",
    asset: fs.Asset != null ? String(fs.Asset) : "",
    field: fs.Field != null ? String(fs.Field) : "",
  }));
}

export function qcChipsFromFailedPlot(plot) {
  return failedSamplesFromPlot(plot)
    .map((s) => ({
      id: s.id || "",
      fluidSampleID: String(s.fluidSampleId || "").trim(),
    }))
    .filter((c) => c.fluidSampleID || c.id);
}

function dedupeSampleChips(chips) {
  const seen = new Set();
  const out = [];
  for (const c of chips) {
    const key = (c.fluidSampleID || c.id || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({ id: c.id, fluidSampleID: c.fluidSampleID });
  }
  return out;
}

/** Two-bar violation plots: chips only from FailedSamples; per-sample lists use the distribution-samples API. */
function buildViolationTwoBarChipSlots(plot) {
  const failedChips = dedupeSampleChips(qcChipsFromFailedPlot(plot).map((c) => ({ ...c })));
  const dist = [[], failedChips.map((c) => ({ ...c }))];
  return {
    outsideRangeChipFlags: [false, true],
    outsideRangeChipsByCategory: dist.map((col) => col.map((c) => ({ ...c }))),
    distributionChipsByCategory: dist.map((col) => col.map((c) => ({ ...c }))),
  };
}

function uniqueSortedIndices(indices) {
  return [...new Set((indices || []).filter((i) => Number.isFinite(i) && i >= 0))].sort((a, b) => a - b);
}

/**
 * Per-category sample chips for distribution bars. Mapper only attaches FailedSamples on red bars;
 * binned outside-range IDs load via the distribution-samples API (name + range).
 */
function buildOutsideChipSlots(categoryCount, redZeroBasedIndices, plot, _categoryKeysRaw = null) {
  if (!categoryCount || !redZeroBasedIndices?.length) {
    return {
      outsideRangeChipFlags: null,
      outsideRangeChipsByCategory: null,
      distributionChipsByCategory: null,
    };
  }
  const reds = uniqueSortedIndices(redZeroBasedIndices);
  const failedChips = qcChipsFromFailedPlot(plot).map((c) => ({ ...c }));
  const flags = Array.from({ length: categoryCount }, (_, i) => reds.includes(i));

  const distributionChipsByCategory = Array.from({ length: categoryCount }, (_, i) => {
    if (reds.includes(i) && failedChips.length) return failedChips.map((c) => ({ ...c }));
    return [];
  });

  const outsideRangeChipsByCategory = Array.from({ length: categoryCount }, (_, i) =>
    reds.includes(i) ? distributionChipsByCategory[i] : []
  );

  return {
    outsideRangeChipFlags: flags,
    outsideRangeChipsByCategory,
    distributionChipsByCategory,
  };
}

function keysFromPlot(plot) {
  if (!plot) return [];
  const raw =
    plot.DistributionPlotKeys ??
    plot.distributionPlotKeys ??
    plot.distribution_plot_keys;
  if (!Array.isArray(raw)) return [];
  return raw.map((k) => {
    const keyRaw = k.Key !== undefined && k.Key !== null ? k.Key : k.key;
    const valRaw = k.Value !== undefined && k.Value !== null ? k.Value : k.value;
    return {
      Key: typeof keyRaw === "string" ? keyRaw.trim() : keyRaw != null ? String(keyRaw).trim() : "",
      Value: valRaw,
    };
  });
}

function distributionKeyNorm(k) {
  return String(k.Key || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Exclude N/A (and similar) from bar chart series */
function filterPlotKeys(keys) {
  return keys.filter((k) => {
    const label = String(k.Key || "");
    return !/^n\/a$/i.test(label.trim());
  });
}

function filterNaLegendItems(items) {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    const t = String(item?.label ?? "").trim();
    return !/^n\/a(\s|\(|$)/i.test(t);
  });
}

function categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, options = {}) {
  return {
    plotKind: "categoricalBar",
    xAxisTitle: plot.DisplayName || plot.Name || "",
    rawValues: [],
    categories,
    categoryCounts: counts,
    barColors: colors,
    legendItems: filterNaLegendItems(legendItems || []),
    summaryStats,
    showBinsSlider: false,
    validRange: {
      values: counts,
      label: "Samples",
      count: counts.reduce((a, b) => a + b, 0),
      percent: "",
    },
    outsideRange: { count: "-", label: "" },
    yAxisTitle: options.yAxisTitle || "No. of Samples",
    gridStyle: options.gridStyle || "default",
    outsideRangeChipFlags: options.outsideRangeChipFlags ?? null,
    outsideRangeChipsByCategory: options.outsideRangeChipsByCategory ?? null,
    distributionChipsByCategory: options.distributionChipsByCategory ?? null,
    failedHistogramChips: null,
    distributionPlotApiName: String(plot?.Name ?? plot?.name ?? "").trim(),
    distributionCategoryApiPlotKeys: options.distributionCategoryApiPlotKeys ?? null,
  };
}

function statsFromValues(values) {
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const sum = values.reduce((a, b) => a + b, 0);
  const avg = sum / values.length;
  return { min, max, avg };
}

function summaryStatsFromRawValues(plot, summaryType, accentColor = QA_QC_BLUE) {
  const valueList = valuesFromPlot(plot);
  if (valueList.length === 0) return null;
  return buildHistogramValueSummary(summaryType, valueList, accentColor);
}

function mapMudResFMoleFraction(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const display = ["Minor (≤5%)", "Moderate (5-8%)", "Serious (>8%)"];
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const colors = [QA_QC_GREEN, QA_QC_ORANGE, QA_QC_RED];
  const sumBars = counts.reduce((a, b) => a + b, 0);
  const pct0 = sumBars ? Math.round(((counts[0] || 0) / sumBars) * 1000) / 10 : 0;
  const legendItems = [
    {
      color: colors[0],
      label: `Minor Contamination ≤5% (${counts[0]}${sumBars ? `, ${pct0}%` : ""})`,
    },
    { color: colors[1], label: `Moderate 5-8% (${counts[1]})` },
    { color: colors[2], label: `Serious >8% (${counts[2]})` },
  ];
  const chipSlots = buildOutsideChipSlots(3, [2], plot);
  return categoricalBarBase(plot, display, counts, colors, legendItems, null, chipSlots);
}

function yFunctionKeyBucket(keyStr) {
  const n = String(keyStr || "")
    .replace(/\u2265/g, ">=")
    .replace(/\u2264/g, "<=")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!n) return null;
  if (/^good\b/.test(n) || />\s*0\.9/.test(n) || />=\s*0\.9/.test(n)) return "good";
  if (/^moderate\b/.test(n) || /0\.7.*0\.9/.test(n)) return "moderate";
  if (/^poor\b/.test(n) || /<\s*0\.7/.test(n)) return "poor";
  return null;
}

function mapYFunction(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const display = ["Good (≥0.9)", "Moderate (0.7-0.9)", "Poor (<0.7)"];
  const buckets = ["good", "moderate", "poor"];
  const countsByBucket = { good: 0, moderate: 0, poor: 0 };
  const apiKeysByBucket = { good: "", moderate: "", poor: "" };
  for (const k of keys) {
    const bucket = yFunctionKeyBucket(k.Key);
    if (!bucket) continue;
    countsByBucket[bucket] = toNum(k.Value) || 0;
    apiKeysByBucket[bucket] = String(k.Key ?? "").trim();
  }
  const counts = buckets.map((b) => countsByBucket[b] || 0);
  const distributionCategoryApiPlotKeys = buckets.map((b) => apiKeysByBucket[b] || "");
  const colors = [QA_QC_GREEN, QA_QC_ORANGE, QA_QC_RED];
  const total = counts.reduce((a, b) => a + b, 0);
  const legendItems = display.map((label, i) => {
    const c = counts[i];
    const pct = total ? Math.round((c / total) * 1000) / 10 : 0;
    const pctPart = i === 0 && total ? `, ${pct}%` : "";
    return {
      color: colors[i],
      label:
        i === 0
          ? `Good Linearity R² ≥ 0.9 (${c} samples${pctPart})`
          : i === 1
            ? `Moderate Linearity R² 0.7-0.9 (${c} samples)`
            : `Poor Linearity R² < 0.7 (${c} samples)`,
    };
  });
  const chipSlots = buildOutsideChipSlots(3, [2], plot);
  return categoricalBarBase(plot, display, counts, colors, legendItems, null, {
    ...chipSlots,
    distributionCategoryApiPlotKeys,
  });
}

/** CCETestsGas — outer density bins red, inner green (N/A excluded from bars). */
function mapGasDensityPsat(plot) {
  return mapOilDensityPsat(plot, "gasDensityGcm3");
}

/** CCETestsGas — Z factor: tails (< 0.6, > 0.9) red, middle bins green. */
function mapGasZFactorPsat(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const n = categories.length;
  const colors = categories.map((_, i) => (i === 0 || i === n - 1 ? QA_QC_RED : QA_QC_GREEN));
  const within = counts.slice(1, -1).reduce((a, b) => a + b, 0);
  const outside = (counts[0] || 0) + (counts[n - 1] || 0);
  const total = within + outside;
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `Within Range: 0.6-0.9 (${within} samples${total ? `, ${Math.round((within / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `Outside Range (${outside} samples${total ? `, ${Math.round((outside / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const summaryStats = summaryStatsFromRawValues(plot, "gasZFactor", QA_QC_BLUE);
  const redIdx = n <= 1 ? [0] : [0, n - 1];
  const chipSlots = buildOutsideChipSlots(n, redIdx, plot, rawKeys);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, chipSlots);
}

function mapRelativeVolumViolation(plot) {
  const keys = keysFromPlot(plot);
  const passed = keys.find((k) => /^passed$/i.test(String(k.Key || "").trim()));
  const failed = keys.find((k) => /^failed$/i.test(String(k.Key || "").trim()));
  const categories = ["No Violations", "At Least One Violation"];
  const counts = [toNum(passed?.Value) || 0, toNum(failed?.Value) || 0];
  const colors = [QA_QC_GREEN, QA_QC_RED];
  const passedApiKey = passed ? String(passed.Key ?? "").trim() : "";
  const failedApiKey = failed ? String(failed.Key ?? "").trim() : "";
  const distributionCategoryApiPlotKeys = [passedApiKey || "Passed", failedApiKey || "Failed"];
  const total = counts[0] + counts[1];
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `No Violations (${counts[0]}${total ? `, ${Math.round((counts[0] / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `At Least One Violation (${counts[1]}${total ? `, ${Math.round((counts[1] / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const chipSlots = buildViolationTwoBarChipSlots(plot);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, null, {
    ...chipSlots,
    distributionCategoryApiPlotKeys,
  });
}

function mapVtotViolation(plot) {
  const keys = keysFromPlot(plot);
  const passed = keys.find((k) => /^passed$/i.test(String(k.Key || "").trim()));
  const failed = keys.find((k) => /^failed$/i.test(String(k.Key || "").trim()));
  if (passed || failed) {
    return mapRelativeVolumViolation(plot);
  }
  const reported = keys.find((k) => distributionKeyNorm(k) === "reported");
  const notReported = keys.find((k) => distributionKeyNorm(k) === "not reported");
  const categories = ["No Violations", "At Least One Violation"];
  const counts = [toNum(notReported?.Value) || 0, toNum(reported?.Value) || 0];
  const colors = [QA_QC_GREEN, QA_QC_RED];
  const distributionCategoryApiPlotKeys = [
    notReported ? String(notReported.Key ?? "").trim() || "Not reported" : "Not reported",
    reported ? String(reported.Key ?? "").trim() || "Reported" : "Reported",
  ];
  const total = counts[0] + counts[1];
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `No Violations (${counts[0]}${total ? `, ${Math.round((counts[0] / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `At Least One Violation (${counts[1]}${total ? `, ${Math.round((counts[1] / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const chipSlots = buildViolationTwoBarChipSlots(plot);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, null, {
    ...chipSlots,
    distributionCategoryApiPlotKeys,
  });
}

/** First + last category bar red, middle green (by index among visible bins) */
function mapOilDensityPsat(plot, densitySummaryKey = "oilDensityGcm3") {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const n = categories.length;
  const colors = categories.map((_, i) => (i === 0 || i === n - 1 ? QA_QC_RED : QA_QC_GREEN));
  const within = counts.slice(1, -1).reduce((a, b) => a + b, 0);
  const outside = (counts[0] || 0) + (counts[n - 1] || 0);
  const total = within + outside;
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `Within Range (${within} samples${total ? `, ${Math.round((within / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `Outside Range (${outside} samples${total ? `, ${Math.round((outside / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const summaryStats = summaryStatsFromRawValues(plot, densitySummaryKey, QA_QC_BLUE);
  const redIdx = n <= 1 ? [0] : [0, n - 1];
  const chipSlots = buildOutsideChipSlots(n, redIdx, plot, rawKeys);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, chipSlots);
}

function mapOilCompressibilityPsat(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const n = categories.length;
  const colors = categories.map((_, i) => (i === 0 || i === n - 1 ? QA_QC_RED : QA_QC_GREEN));
  const within = counts.slice(1, -1).reduce((a, b) => a + b, 0);
  const outside = (counts[0] || 0) + (counts[n - 1] || 0);
  const total = within + outside;
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `Within Range: 5-25 (${within} samples${total ? `, ${Math.round((within / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `Outside Range (${outside} samples${total ? `, ${Math.round((outside / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const summaryStats = summaryStatsFromRawValues(plot, "compressibilityMicroPsi", QA_QC_BLUE);
  const redIdx = n <= 1 ? [0] : [0, n - 1];
  const chipSlots = buildOutsideChipSlots(n, redIdx, plot, rawKeys);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, chipSlots);
}

function mapOilCompressibilityPres(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const n = categories.length;
  const colors = categories.map((_, i) => (i === 0 || i === n - 1 ? QA_QC_RED : QA_QC_GREEN));
  const within = counts.slice(1, -1).reduce((a, b) => a + b, 0);
  const outside = (counts[0] || 0) + (counts[n - 1] || 0);
  const total = within + outside;
  const legendItems = [
    {
      color: QA_QC_GREEN,
      label: `Within Range: 3-15 (${within} samples${total ? `, ${Math.round((within / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `Outside Range (${outside} samples${total ? `, ${Math.round((outside / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const summaryStats = summaryStatsFromRawValues(plot, "compressibilityMicroPsi", QA_QC_BLUE);
  const redIdx = n <= 1 ? [0] : [0, n - 1];
  const chipSlots = buildOutsideChipSlots(n, redIdx, plot, rawKeys);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, chipSlots);
}

function toPercentLike(v) {
  const x = Number(v);
  if (!Number.isFinite(x)) return NaN;
  return Math.abs(x) <= 1 && Math.abs(x) > 1e-9 ? x * 100 : x;
}

/** Midpoint % for histogram-style keys like "< 1 %", "1-2 %", "> 5 %" (matches bar buckets). */
function percentBinMidpointFromKey(keyStr) {
  const s = String(keyStr || "")
    .trim()
    .replace(/\u2013/g, "-")
    .replace(/%/g, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
  const within = s.match(/^within\s*([\d.]+)/);
  if (within) {
    const v = Number(within[1]);
    if (Number.isFinite(v)) return v / 2;
  }
  const lt = s.match(/^<\s*([\d.]+)/);
  if (lt) return Number(lt[1]) / 2;
  const rg = s.match(/^([\d.]+)\s*-\s*([\d.]+)/);
  if (rg) {
    const a = Number(rg[1]);
    const b = Number(rg[2]);
    if (Number.isFinite(a) && Number.isFinite(b)) return (a + b) / 2;
  }
  const gt = s.match(/^>\s*([\d.]+)/);
  if (gt) return Number(gt[1]) + 2.5;
  return NaN;
}

function summaryStatsDiscrepancyFromDistributionBins(plot, keysFiltered, counts, accentColor) {
  let n = 0;
  let weighted = 0;
  let within = 0;
  let above = 0;
  for (let i = 0; i < keysFiltered.length; i++) {
    const c = toNum(counts[i]) || 0;
    if (c <= 0) continue;
    const mid = percentBinMidpointFromKey(keysFiltered[i].Key);
    if (!Number.isFinite(mid)) continue;
    n += c;
    weighted += mid * c;
    if (mid <= 5) within += c;
    else above += c;
  }
  if (n > 0) {
    const avg = weighted / n;
    return {
      accentColor,
      rows: [
        { label: "Average Discrepancy:", value: `${fmtNum(avg)}%`, emphasize: true },
        {
          label: "Within 5%:",
          value: `${Math.round((within / n) * 1000) / 10}%`,
          emphasize: false,
          valueColor: QA_QC_GREEN,
        },
        {
          label: "Above 5%:",
          value: `${Math.round((above / n) * 1000) / 10}%`,
          emphasize: false,
          valueColor: QA_QC_RED,
        },
      ],
    };
  }
  const vals = valuesFromPlot(plot);
  if (!vals.length) return null;
  const pctVals = vals.map(toPercentLike).filter((x) => !Number.isNaN(x));
  if (!pctVals.length) return null;
  const avg = pctVals.reduce((a, b) => a + b, 0) / pctVals.length;
  const nv = pctVals.length;
  const withinV = pctVals.filter((p) => p <= 5).length;
  const aboveV = nv - withinV;
  return {
    accentColor,
    rows: [
      { label: "Average Discrepancy:", value: `${fmtNum(avg)}%`, emphasize: true },
      {
        label: "Within 5%:",
        value: `${nv ? Math.round((withinV / nv) * 1000) / 10 : 0}%`,
        emphasize: false,
        valueColor: QA_QC_GREEN,
      },
      {
        label: "Above 5%:",
        value: `${nv ? Math.round((aboveV / nv) * 1000) / 10 : 0}%`,
        emphasize: false,
        valueColor: QA_QC_RED,
      },
    ],
  };
}

function mapForwardBackwardMoles(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  let redIdx = rawKeys
    .map((key, idx) => (isForwardBackwardMolesFailKey(key) ? idx : -1))
    .filter((idx) => idx >= 0);
  // API omits the failed bin when no samples fail; synthesize it so the red category stays clickable.
  if (!redIdx.length) {
    categories.push(">5 %");
    counts.push(0);
    rawKeys.push(">5");
    redIdx = [categories.length - 1];
  }
  const colors = categories.map((_, idx) => (redIdx.includes(idx) ? QA_QC_RED : QA_QC_BLUE));
  const compliantCount = counts
    .filter((_, idx) => !redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const failedCount = counts
    .filter((_, idx) => redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const total = compliantCount + failedCount;
  const legendItems = [
    {
      color: QA_QC_BLUE,
      label: `<=5 % (${compliantCount}${total ? `, ${Math.round((compliantCount / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `>5 % (${failedCount}${total ? `, ${Math.round((failedCount / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const chipSlots =
    redIdx.length > 0 ? buildOutsideChipSlots(categories.length, redIdx, plot, rawKeys) : {};
  const summaryStats = summaryStatsDiscrepancyFromDistributionBins(plot, keys, counts, QA_QC_BLUE);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, {
    ...chipSlots,
    distributionCategoryApiPlotKeys: rawKeys,
  });
}

function isForwardBackwardMolesFailKey(keyStr) {
  const normalized = String(keyStr || "")
    .replace(/\u2013/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!normalized) return false;
  if (/>\s*5(?:\D|$)/.test(normalized)) return true;
  if (/above\s*5(?:\D|$)/.test(normalized)) return true;
  if (/greater\s*than\s*5(?:\D|$)/.test(normalized)) return true;
  return false;
}

function mapSTODensityError(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  let redIdx = rawKeys
    .map((key, idx) => (isPercentThresholdFailKey(key, 5) ? idx : -1))
    .filter((idx) => idx >= 0);
  // API omits the failed bin when no samples fail; synthesize it so the red category stays clickable.
  if (!redIdx.length) {
    categories.push(">5 %");
    counts.push(0);
    rawKeys.push(">5");
    redIdx = [categories.length - 1];
  }
  const colors = categories.map((_, idx) => (redIdx.includes(idx) ? QA_QC_RED : QA_QC_BLUE));
  const compliantCount = counts
    .filter((_, idx) => !redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const failedCount = counts
    .filter((_, idx) => redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const total = compliantCount + failedCount;
  const legendItems = [
    {
      color: QA_QC_BLUE,
      label: `<=5 % (${compliantCount}${total ? `, ${Math.round((compliantCount / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `>5 % (${failedCount}${total ? `, ${Math.round((failedCount / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const chipSlots =
    redIdx.length > 0 ? buildOutsideChipSlots(categories.length, redIdx, plot, rawKeys) : {};
  const summaryStats = summaryStatsDiscrepancyFromDistributionBins(plot, keys, counts, QA_QC_BLUE);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, {
    ...chipSlots,
    distributionCategoryApiPlotKeys: rawKeys,
  });
}

function mapRsError(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  let redIdx = rawKeys
    .map((key, idx) => (isPercentThresholdFailKey(key, 10) ? idx : -1))
    .filter((idx) => idx >= 0);
  // API omits the failed bin when no samples fail; synthesize it so the red category stays clickable.
  if (!redIdx.length) {
    categories.push(">10 %");
    counts.push(0);
    rawKeys.push(">10");
    redIdx = [categories.length - 1];
  }
  const colors = categories.map((_, idx) => (redIdx.includes(idx) ? QA_QC_RED : QA_QC_BLUE));
  const compliantCount = counts
    .filter((_, idx) => !redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const failedCount = counts
    .filter((_, idx) => redIdx.includes(idx))
    .reduce((sum, n) => sum + n, 0);
  const total = compliantCount + failedCount;
  const legendItems = [
    {
      color: QA_QC_BLUE,
      label: `<=10 % (${compliantCount}${total ? `, ${Math.round((compliantCount / total) * 1000) / 10}%` : ""})`,
    },
    {
      color: QA_QC_RED,
      label: `>10 % (${failedCount}${total ? `, ${Math.round((failedCount / total) * 1000) / 10}%` : ""})`,
    },
  ];
  const chipSlots =
    redIdx.length > 0 ? buildOutsideChipSlots(categories.length, redIdx, plot, rawKeys) : {};
  const summaryStats = summaryStatsRsFromDistributionBins(plot, keys, counts, QA_QC_BLUE);
  return categoricalBarBase(plot, categories, counts, colors, legendItems, summaryStats, {
    ...chipSlots,
    distributionCategoryApiPlotKeys: rawKeys,
  });
}

function summaryStatsRsFromDistributionBins(plot, keysFiltered, counts, accentColor) {
  let n = 0;
  let weighted = 0;
  let within10 = 0;
  let above10 = 0;
  for (let i = 0; i < keysFiltered.length; i++) {
    const c = toNum(counts[i]) || 0;
    if (c <= 0) continue;
    const mid = percentBinMidpointFromKey(keysFiltered[i].Key);
    if (!Number.isFinite(mid)) continue;
    n += c;
    weighted += mid * c;
    if (mid <= 10) within10 += c;
    else above10 += c;
  }
  if (n > 0) {
    const avg = weighted / n;
    return {
      accentColor,
      rows: [
        { label: "Average Rs Error:", value: `${fmtNum(avg)}%`, emphasize: true },
        { label: "Within 10%:", value: `${Math.round((within10 / n) * 1000) / 10}%`, emphasize: false, valueColor: QA_QC_GREEN },
        { label: "Above 10%:", value: `${Math.round((above10 / n) * 1000) / 10}%`, emphasize: false, valueColor: QA_QC_RED },
      ],
    };
  }
  const vals = valuesFromPlot(plot);
  if (!vals.length) return null;
  const pctVals = vals.map(toPercentLike).filter((x) => !Number.isNaN(x));
  if (!pctVals.length) return null;
  const avg = pctVals.reduce((a, b) => a + b, 0) / pctVals.length;
  const nv = pctVals.length;
  const within10V = pctVals.filter((p) => p <= 10).length;
  const above10V = pctVals.filter((p) => p > 10).length;
  return {
    accentColor,
    rows: [
      { label: "Average Rs Error:", value: `${fmtNum(avg)}%`, emphasize: true },
      { label: "Within 10%:", value: `${nv ? Math.round((within10V / nv) * 1000) / 10 : 0}%`, emphasize: false, valueColor: QA_QC_GREEN },
      { label: "Above 10%:", value: `${nv ? Math.round((above10V / nv) * 1000) / 10 : 0}%`, emphasize: false, valueColor: QA_QC_RED },
    ],
  };
}

function isPercentThresholdFailKey(keyStr, threshold) {
  const normalized = String(keyStr || "")
    .replace(/\u2013/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  if (!normalized || !Number.isFinite(threshold)) return false;

  const gtMatch = normalized.match(/^>\s*([\d.]+)/);
  if (gtMatch) return Number(gtMatch[1]) >= threshold;

  if (/^above\s*[\d.]+/.test(normalized) || /^greater than\s*[\d.]+/.test(normalized)) {
    const m = normalized.match(/([\d.]+)/);
    if (m) return Number(m[1]) >= threshold;
  }

  const rangeMatch = normalized.match(/^([\d.]+)\s*-\s*([\d.]+)/);
  if (rangeMatch) {
    const from = Number(rangeMatch[1]);
    if (Number.isFinite(from)) return from >= threshold;
  }

  return false;
}

function feedDeviationColorForKey(keyStr) {
  const s = String(keyStr || "").toLowerCase();
  if (/^<\s*10|under.*10/.test(s) || s.includes("< 10")) return QA_QC_GREEN;
  if (/10\s*-\s*20|10 to 20/.test(s)) return QA_QC_ORANGE;
  return QA_QC_RED;
}

function mapOverallFeedDeviation(plot) {
  const keys = filterPlotKeys(keysFromPlot(plot));
  const rawKeys = keys.map((k) => String(k.Key || "").trim());
  const categories = keys.map((k) => String(k.Key || "").replace(/-/g, " - "));
  const counts = keys.map((k) => toNum(k.Value) || 0);
  const colors = keys.map((k) => feedDeviationColorForKey(k.Key));
  const total = counts.reduce((a, b) => a + b, 0);
  const legendItems = categories.map((cat, i) => {
    const c = counts[i];
    const pct = total ? Math.round((c / total) * 1000) / 10 : 0;
    const short = cat.replace(/\s*%$/, "").trim();
    return {
      color: colors[i],
      label: `${short}: ${c}${total ? ` (${pct}%)` : ""}`,
    };
  });
  const redIdx = colors.map((c, i) => (c === QA_QC_RED ? i : -1)).filter((i) => i >= 0);
  const chipSlots = redIdx.length
    ? buildOutsideChipSlots(categories.length, redIdx, plot, rawKeys)
    : {};
  return categoricalBarBase(plot, categories, counts, colors, legendItems, null, chipSlots);
}

function mapGenericKeys(plot) {
  const filtered = filterPlotKeys(keysFromPlot(plot));
  if (filtered.length === 0) return null;
  const rawKeys = filtered.map((k) => String(k.Key || "").trim());
  const categories = filtered.map((k) => String(k.Key || ""));
  const counts = filtered.map((k) => toNum(k.Value) || 0);
  const colors = categories.map(() => QA_QC_BLUE);
  const n = categories.length;
  const chips = qcChipsFromFailedPlot(plot);
  const chipSlots = chips.length && n ? buildOutsideChipSlots(n, [n - 1], plot, rawKeys) : {};
  return categoricalBarBase(plot, categories, counts, colors, [], null, chipSlots);
}

/** When DistributionPlotKeys is absent: histogram of Values; bin count from UI (default 10). Bar color / summary by plot name. */
const VALUES_ONLY_HISTOGRAM_BY_NAME = {
  TestTemperature: { color: QA_QC_BLUE, summary: "tempF" },
  TestTemperatureDL: { color: QA_QC_BLUE, summary: "tempF" },
  PressureStepCount: { color: QA_QC_BLUE, summary: "steps" },
  OilSaturationPressure: { color: QA_QC_BLUE, summary: "psat" },
  OilSaturationPressureDL: { color: QA_QC_BLUE, summary: "psat" },
  DewPoint: { color: QA_QC_BLUE, summary: "dewPsia" },
  ΑΡΙ: { color: QA_QC_BLUE, summary: "apiDegrees" },
  API: { color: QA_QC_BLUE, summary: "apiDegrees" },
  SolutionGOR: { color: QA_QC_BLUE, summary: "gorScfStb" },
  OilFVFPsat: { color: QA_QC_BLUE, summary: "fvfBblStb" },
  ResidualOilDensity: { color: QA_QC_BLUE, summary: "residualOilDensityGcm3" },
};

function buildHistogramValueSummary(summaryType, values, accentColor) {
  const st = statsFromValues(values);
  if (!st) return null;
  switch (summaryType) {
    case "tempF":
      return {
        accentColor,
        rows: [
          { label: "Average Temperature:", value: `${fmtNum(st.avg)}°F`, emphasize: true },
          { label: "Min Temperature:", value: `${fmtNum(st.min)}°F`, emphasize: false },
          { label: "Max Temperature:", value: `${fmtNum(st.max)}°F`, emphasize: false },
        ],
      };
    case "steps":
      return {
        accentColor,
        rows: [
          { label: "Average Steps:", value: fmtNum(st.avg), emphasize: true },
          { label: "Min Steps:", value: fmtNum(st.min), emphasize: false },
          { label: "Max Steps:", value: fmtNum(st.max), emphasize: false },
        ],
      };
    case "psat":
      return {
        accentColor,
        rows: [
          { label: "Average Psat:", value: `${fmtNum(st.avg)} psia`, emphasize: true },
          { label: "Min Psat:", value: `${fmtNum(st.min)} psia`, emphasize: false },
          { label: "Max Psat:", value: `${fmtNum(st.max)} psia`, emphasize: false },
        ],
      };
    case "dewPsia":
      return {
        accentColor,
        rows: [
          { label: "Average Dew Point:", value: `${fmtNum(st.avg)} psia`, emphasize: true },
          { label: "Min Dew Point:", value: `${fmtNum(st.min)} psia`, emphasize: false },
          { label: "Max Dew Point:", value: `${fmtNum(st.max)} psia`, emphasize: false },
        ],
      };
    case "oilDensityGcm3":
      return {
        accentColor,
        rows: [
          { label: "Average Oil Density:", value: `${fmtNum(st.avg)} g/cm³`, emphasize: true },
          { label: "Min Oil Density:", value: `${fmtNum(st.min)} g/cm³`, emphasize: false },
          { label: "Max Oil Density:", value: `${fmtNum(st.max)} g/cm³`, emphasize: false },
        ],
      };
    case "gasDensityGcm3":
      return {
        accentColor,
        rows: [
          { label: "Average Gas Density:", value: `${fmtNum(st.avg)} g/cm³`, emphasize: true },
          { label: "Min Gas Density:", value: `${fmtNum(st.min)} g/cm³`, emphasize: false },
          { label: "Max Gas Density:", value: `${fmtNum(st.max)} g/cm³`, emphasize: false },
        ],
      };
    case "gasZFactor":
      return {
        accentColor,
        rows: [
          { label: "Average Gas Z Factor:", value: fmtNum(st.avg), emphasize: true },
          { label: "Min Gas Z Factor:", value: fmtNum(st.min), emphasize: false },
          { label: "Max Gas Z Factor:", value: fmtNum(st.max), emphasize: false },
        ],
      };
    case "apiDegrees":
      return {
        accentColor,
        rows: [
          { label: "Average API:", value: `${fmtNum(st.avg)}°API`, emphasize: true },
          { label: "Min API:", value: `${fmtNum(st.min)}°API`, emphasize: false },
          { label: "Max API:", value: `${fmtNum(st.max)}°API`, emphasize: false },
        ],
      };
    case "gorScfStb":
      return {
        accentColor,
        rows: [
          { label: "Average GOR:", value: `${fmtNum(st.avg)} scf/stb`, emphasize: true },
          { label: "Min GOR:", value: `${fmtNum(st.min)} scf/stb`, emphasize: false },
          { label: "Max GOR:", value: `${fmtNum(st.max)} scf/stb`, emphasize: false },
        ],
      };
    case "fvfBblStb":
      return {
        accentColor,
        rows: [
          { label: "Average Oil FVF:", value: `${fmtNum(st.avg)} bbl/stb`, emphasize: true },
          { label: "Min Oil FVF:", value: `${fmtNum(st.min)} bbl/stb`, emphasize: false },
          { label: "Max Oil FVF:", value: `${fmtNum(st.max)} bbl/stb`, emphasize: false },
        ],
      };
    case "residualOilDensityGcm3":
      return {
        accentColor,
        rows: [
          {
            label: "Average Residual Oil Density:",
            value: `${fmtNum(st.avg)} g/cm³`,
            emphasize: true,
          },
          { label: "Min Residual Oil Density:", value: `${fmtNum(st.min)} g/cm³`, emphasize: false },
          { label: "Max Residual Oil Density:", value: `${fmtNum(st.max)} g/cm³`, emphasize: false },
        ],
      };
    case "compressibilityMicroPsi":
      return {
        accentColor,
        rows: [
          {
            label: "Average Oil Compressibility:",
            value: `${fmtNum(st.avg)} ×10⁻⁶ /psi`,
            emphasize: true,
          },
          { label: "Min Oil Compressibility:", value: `${fmtNum(st.min)} ×10⁻⁶ /psi`, emphasize: false },
          { label: "Max Oil Compressibility:", value: `${fmtNum(st.max)} ×10⁻⁶ /psi`, emphasize: false },
        ],
      };
    default:
      return null;
  }
}

function mapValuesOnlyHistogram(plot, name) {
  const cfg = VALUES_ONLY_HISTOGRAM_BY_NAME[name];
  const valueList = valuesFromPlot(plot);
  if (valueList.length === 0) return unavailableViewData(plot);
  const color = cfg?.color ?? QA_QC_BLUE;
  const summaryStats =
    cfg?.summary != null ? buildHistogramValueSummary(cfg.summary, valueList, color) : null;
  return mapHistogramRaw(plot, {
    histogramBarColor: color,
    summaryStats,
    showBinsSlider: true,
  });
}

function mapHistogramRaw(plot, options = {}) {
  const valueList = valuesFromPlot(plot);
  if (valueList.length === 0) return unavailableViewData(plot);
  const histChips = qcChipsFromFailedPlot(plot);
  return {
    plotKind: "histogram",
    xAxisTitle: plot.DisplayName || plot.Name || "",
    rawValues: valueList,
    categories: [],
    categoryCounts: [],
    barColors: [],
    legendItems: [],
    summaryStats: options.summaryStats ?? null,
    showBinsSlider: options.showBinsSlider !== false,
    histogramBarColor: options.histogramBarColor ?? QA_QC_BLUE,
    validRange: {
      values: [],
      label: "Samples",
      count: valueList.length,
      percent: "",
    },
    outsideRange: { count: "-", label: "" },
    outsideRangeChipFlags: null,
    outsideRangeChipsByCategory: null,
    distributionChipsByCategory: null,
    failedHistogramChips: histChips.length ? histChips : null,
    distributionPlotApiName: String(plot?.Name ?? plot?.name ?? "").trim(),
    distributionCategoryApiPlotKeys: null,
  };
}

/**
 * Maps API DistributionPlots entry to view model for QA/QC charts.
 * Requires at least one numeric entry in `Values`; otherwise returns unavailable (UI: data not available).
 * Prefer DistributionPlotKeys when present; otherwise histogram from `Values` with UI bin count.
 */
export function mapDistributionPlotToViewData(plot) {
  if (!plot) return null;
  if (valuesFromPlot(plot).length === 0) {
    return unavailableViewData(plot);
  }

  const name = String(plot.Name ?? plot.name ?? "").trim();

  const keys = keysFromPlot(plot);
  const hasKeys = keys.length > 0;

  switch (name) {
    case "MudResFMoleFraction":
      if (hasKeys) return mapMudResFMoleFraction(plot);
      break;
    case "YFunction":
      if (hasKeys) return mapYFunction(plot);
      break;
    case "GasDensityPsat":
      if (hasKeys) return mapGasDensityPsat(plot);
      break;
    case "GasZFactorPsat":
      if (hasKeys) return mapGasZFactorPsat(plot);
      break;
    case "RelativeVolumViolation":
      if (hasKeys) return mapRelativeVolumViolation(plot);
      break;
    case "VtotViolation":
      if (hasKeys) return mapVtotViolation(plot);
      break;
    case "OilDensityPsat":
      if (hasKeys) return mapOilDensityPsat(plot);
      break;
    case "OilCompressibilityPsat":
      if (hasKeys) return mapOilCompressibilityPsat(plot);
      break;
    case "OilCompressibilityPres":
      if (hasKeys) return mapOilCompressibilityPres(plot);
      break;
    case "OilDensityPsatDL":
      if (hasKeys) return mapOilDensityPsat(plot);
      break;
    case "ForwardBackwardMoles":
      if (hasKeys) return mapForwardBackwardMoles(plot);
      break;
    case "STODensityError":
      if (hasKeys) return mapSTODensityError(plot);
      break;
    case "RsError":
      if (hasKeys) return mapRsError(plot);
      break;
    case "OverallFeedDeviation":
    case "OverallFeedDeviationDL":
      if (hasKeys) return mapOverallFeedDeviation(plot);
      break;
    default:
      break;
  }

  const generic = mapGenericKeys(plot);
  if (generic) return generic;

  if (!hasKeys) {
    return mapValuesOnlyHistogram(plot, name);
  }

  return mapHistogramRaw(plot);
}

export const DL_MATERIAL_BALANCE_PLOT_ORDER = ["ForwardBackwardMoles", "STODensityError", "RsError"];

export const DL_FEED_DEVIATION_PLOT_NAMES = [
  "OverallFeedDeviation",
  "OverallFeedDeviationDL",
  "FeedDeviationMolePercent",
  "MudResFMoleFraction",
];
